import { useEffect, useState } from 'react'
import { Button, IconButton, Typography, Box, Stack, Link as MLink, FormControlLabel, TextField, InputAdornment, Checkbox, Divider } from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom'
import { useConfirm } from '@components/ConfirmProvider'
import { useAuth } from '@components/AuthContext';
import { useIsMobile } from '@/hooks/Responsive'
import { useLang } from '@/i18n/useLang'
import { buildLangHref } from '@data/nav'

// 카카오 아이콘
function KakaoIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="15" viewBox="0 0 16 15" fill="none">
      <path d="M8.00005 0C3.5815 0 0 2.76707 0 6.17981C0 8.30224 1.38525 10.1733 3.4947 11.2862L2.60715 14.5284C2.52873 14.8149 2.85638 15.0433 3.10798 14.8773L6.99856 12.3095C7.32688 12.3412 7.66054 12.3597 8.00005 12.3597C12.4182 12.3597 16 9.59272 16 6.17981C16 2.76707 12.4182 0 8.00005 0Z" fill="black"/>
    </svg>
  )
}
// 네이버 아이콘
function NaverIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M10.8491 8.56267L4.91687 0H0V16H5.15088V7.436L11.0831 16H16V0H10.8491V8.56267Z" fill="white"/>
    </svg>
  )
}
// Google 아이콘
function GoogleIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 48 48">
      <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
      <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
      <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
      <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
    </svg>
  )
}

export default function Login() {
  const { isMobile } = useIsMobile()
  const lang = useLang()
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.redirectTo;
  const { REACT_APP_API_BASE } = process.env;
  const { alert, confirm } = useConfirm();
  const { user, loading, idpwLogin, snsLogin } = useAuth();

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [errors, setErrors] = useState({ email: '', password: '' })
  const [rememberId, setRememberId] = useState(false)  // 아이디저장

  const [kind, setKind] = useState(null);  // 회원종류
  const [profile, setProfile] = useState(null);  // 간편회원가입(sns)정보

  const [showPassword, setShowPassword] = useState(false)

  const [loginCnt, setLoginCnt] = useState(0)  // 로그인시도 횟수


  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev)
  }

  const isFromSocial = location.state?.fromSocial === true;

  // validation 체크
  const validate = () => {
    let tempErrors = { email: '', password: '' }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    if (!email) {
      tempErrors.email = '이메일을 입력해 주세요.'
    } else if (!emailRegex.test(email)) {
      tempErrors.email = '이메일 형식이 올바르지 않습니다.'
    }

    if (!password) {
      tempErrors.password = '비밀번호를 입력해 주세요.'
    } else if (password.length < 10) {
      tempErrors.password = '비밀번호는 최소 10자 이상이어야 합니다.'
    }

    setErrors(tempErrors)
    return Object.values(tempErrors).every(x => x === '')
  }



  // 이메일 로그인
  const handleSubmit = async (e) => {
    e.preventDefault()
    if (validate()) {
      // 로그인시도 5회 체크
      if (loginCnt >= 5) {
        alert('로그인을 연속 5회 틀리셨습니다.\n아이디/비밀번호 찾기 후 이용해 주세요.');
        return;
      }

      // 제출시 rememberId에 따라 저장/삭제
      if (rememberId) localStorage.setItem('login_id', email);
      else localStorage.removeItem('login_id');

      try {
        const { ok, message, status, inactiveInfo } = await idpwLogin(email, password);

        // 실패 케이스 처리
        if (!ok) {
          // 휴면계정인 경우 (HTTP 423 + accountId/accountIdSeq 포함)
          if (status === 423 && inactiveInfo) {
            const proceed = await confirm({
              title: '장기 미접속 안내',
              message: '6개월이상 장기 미접속으로 휴면계정 상태입니다.\n잠금 해제를 통해 이용을 하실 수 있습니다.',
              confirmText: '계정잠금해제',
              hideCancel: true,
            });

            if (proceed) {
              // 예시: 휴면 해제 페이지로 이동하면서 accountId/accountIdSeq 전달
              navigate(buildLangHref('/inactivity', lang), {
                state: {
                  publicId: inactiveInfo.publicId,
                },
              });
            }

            // 휴면계정이면 더 이상 아래 로직 진행하지 않음
            return;
          }

          // 일반 오류 (비밀번호 틀림 등)
          alert(message);
          setLoginCnt((prev) => prev + 1);
          return;
        }

        // 성공시 메인으로
      //navigate(buildLangHref('/', lang));
      const target = redirectTo || buildLangHref('/', lang);
      navigate(target, { replace: true });

      } catch (error) {
        console.error(error);
      }
    }
  }



  // 간편회원가입 호출
  const snsAuth = (provider) => {
    setKind(provider);
    localStorage.setItem("MemberRegist.kind", provider);
    localStorage.setItem("callBackUrl", "/login");
    window.location.href = `${REACT_APP_API_BASE}/oauth2/authorization/${provider}`;
  };

  useEffect(() => {
    // 아이디저장 관련
    const saved = localStorage.getItem('login_id');
    if (saved) {
      setEmail(saved);
      setRememberId(true);
    }

    // 소셜(SNS) 로그인 콜백관련
    const returnKind = localStorage.getItem("MemberRegist.kind");
    setKind(returnKind);
    if (isFromSocial) {
      const stored = localStorage.getItem("socialProfile");
      if (stored) {
        try {
          const socialProfile = JSON.parse(stored);

          // 프로필 저장
          setProfile(socialProfile);

          // 간편연계(sns) 로그인
          snsMemberLogin(returnKind, socialProfile);

        }
        catch (e) {
          console.error(e);
        }
        finally {
          // localStorage 등록한 데이터 제거
          ["MemberRegist.kind", "socialProfile"].forEach((k) =>
            localStorage.removeItem(k)
          );
        }
      }
    }

  }, []);

  // 간편연계(sns) 로그인
  const snsMemberLogin = async (returnKind, socialProfile) => {
    // sns id 추출
    const snsNaverKey = returnKind == 'naver' ? socialProfile?.response?.id : '';
    const snsKakaoKey = returnKind == 'kakao' ? socialProfile?.id : '';
    const snsGoogleKey = returnKind == 'google' ? socialProfile?.sub : '';

    try {
      const params = {
        memberKindCode : returnKind,
        snsNaverKey : snsNaverKey,
        snsKakaoKey : snsKakaoKey,
        snsGoogleKey : snsGoogleKey,
        snsToken : sessionStorage.getItem("snsToken") || "",
      };
      
      const { ok, message, status, inactiveInfo } = await snsLogin(params);

      // 실패 케이스 처리
      if (!ok) {
        // 휴면계정인 경우 (HTTP 423 + accountId/accountIdSeq 포함)
        if (status === 423 && inactiveInfo) {
          const proceed = await confirm({
            title: '장기 미접속 안내',
            message: '6개월이상 장기 미접속으로 휴면계정 상태입니다.\n잠금 해제를 통해 이용을 하실 수 있습니다.',
            confirmText: '계정잠금해제',
            hideCancel: true,
          });

          if (proceed) {
            // 예시: 휴면 해제 페이지로 이동하면서 accountId/accountIdSeq 전달
            navigate(buildLangHref('/inactivity', lang), {
              state: {
                publicId: inactiveInfo.publicId,
                },
            });
          }

          // 휴면계정이면 더 이상 아래 로직 진행하지 않음
          return;
        }

        // 일반 오류 (비밀번호 틀림 등)
        alert(message);
        setLoginCnt((prev) => prev + 1);
        return;
      }

      // 성공시 메인으로
      sessionStorage.removeItem("snsToken");
      const target = redirectTo || buildLangHref('/', lang);
      navigate(target, { replace: true });

    } catch (error) {
      // console.error(error);
    }
  };

  return (
    <Box>
      <Typography component="h3" variant={isMobile ? 'h6' : 'h5'} sx={{ mb: 7 }}>
        로그인
      </Typography>
    
      {/* 로그인 폼 */}
      <Box component="form" onSubmit={handleSubmit}>
        <Box sx={{ mb: 4 }}>
          <Typography fontWeight={500} component="label" htmlFor="email" sx={{ mb: 0.5 }}>
            아이디(이메일 주소) 
          </Typography>
          <TextField
            placeholder="employee@example.com"
            margin="normal"
            fullWidth
            id="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              // 체크된 상태라면 타이핑 즉시 저장
              if (rememberId) localStorage.setItem('login_id', e.target.value);
            }}
            error={!!errors.email}
            helperText={errors.email}
            name="email"
            autoComplete="email"
            autoFocus
            inputProps={{ maxLength: 40 }}
            sx={{ my: 0 }}
          />
        </Box>
        <Box>
          <Typography fontWeight={500} component="label" htmlFor="password" sx={{ mb: 0.5 }}>
            비밀번호 
          </Typography>
          <TextField
            placeholder="●●●●●●●●"
            margin="normal"
            fullWidth
            name="password"
            type={showPassword ? 'text' : 'password'}
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!errors.password}
            helperText={errors.password}
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ pr: 3 }}>
                  <IconButton
                    edge="end"
                    onClick={handleClickShowPassword}
                  >
                    {showPassword ? <Visibility sx={{ color: 'grey.dark' }} /> : <VisibilityOff sx={{ color: 'grey.dark' }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            inputProps={{ maxLength: 20 }}
            sx={{ my: 0 }}
          />
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              checked={rememberId}
              onChange={(e) => {
                const checked = e.target.checked;
                setRememberId(checked);
                if (checked) localStorage.setItem('login_id', email);
                else localStorage.removeItem('login_id');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSubmit(e)
                }
              }}
            />
          }
          label={<Typography variant="body2" color="text.secondary">아이디 저장</Typography>}
          sx={{ mt: 0.5 }}
        />
        <Button
          type="submit"
          size="large"
          variant="contained"
          color="primary"
          fullWidth
          sx={{ mt: 5, mb: 2 }}
          // onClick={handleSubmit}
        >
          로그인
        </Button>

        <Stack
          direction="row"
          justifyContent="space-between"
          gap={2}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <MLink component={RouterLink} to={buildLangHref('/recoveremailaccount', lang)}>아이디 찾기</MLink>
            <Box sx={{ width: '1px', height: '12px', bgcolor: 'grey.light' }} />
            <MLink component={RouterLink} to={buildLangHref('/recoverpassword', lang)}>비밀번호 찾기</MLink>
          </Stack>
          <MLink component={RouterLink} to={buildLangHref('/signup', lang)}>회원가입</MLink>
        </Stack>
      </Box>

      <Divider sx={{ mt: 9, typography: 'body2' }}>간편로그인</Divider>

      {/* 간편로그인 */}
      <Stack direction="row" spacing={5} alignContent="center" justifyContent="center" sx={{ mt: 6 }}>
        <IconButton
          onClick={() => {snsAuth('kakao')}}
          sx={{
            width: '48px',
            height: '48px',
            bgcolor: '#FEE500',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',

            '&:hover': {
              bgcolor: '#F2D900',
            }
          }}
        >
          <KakaoIcon />
        </IconButton>
        
        <IconButton
          onClick={() => {snsAuth('naver')}}
          sx={{
            width: '48px',
            height: '48px',
            bgcolor: '#03C75A',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',

            '&:hover': {
              bgcolor: '#02B150',
            }
          }}
        >
          <NaverIcon />
        </IconButton>
        
        <IconButton
          onClick={() => {snsAuth('google')}}
          sx={{
            width: '48px',
            height: '48px',
            bgcolor: 'background.paper',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid',
            borderColor: 'divider',

            '&:hover': {
              bgcolor: 'grey.50',
            }
          }}
        >
          <GoogleIcon />
        </IconButton>
      </Stack>
    </Box>
  )
}